import { useEffect, useState } from 'react';
import BulkEmailStepper from '../components/BulkEmailStepper';
import BulkEmailTemplateSelection from '../components/BulkEmailTemplateSelection';
import BulkEmailFilterSelection from '../components/BulkEmailFilterSelection';
import BulkEmailPreviewAndSendNotification from '../components/BulkEmailPreviewAndSendNotification';
import { FiltersState } from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import Loading from '../components/Loading';
import useValidatePayload from '../hooks/useValidateEmailPayload';
import { framePayload } from '../helpers/BulkEmail/frameBulkEmailPayload';
import CustomModal from '../components/CustomModal';
import usePlaceSQSMessage from '../hooks/usePlaceSQSMessage';
import { revertBulkEmailState } from '../helpers/BulkEmail/revertBulkEmailState';
import { steps } from '../constant/constant';
import { bulkEmailPayloadValidator } from '../validator/bulkEmailPayloadValidator';
import showToast from '../components/ToastComponent';
import updateNotificationStatus from '../hooks/useHandleCancelBulkEmailNotification';

const BulkEmailNotification = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [validTemplate, setValidTemplate] = useState<boolean>(false);
  const [filters, setFilters] = useState<FiltersState>();
  const [open, setIsOpen] = useState<boolean>(false);
  const [validCsv, isValidCsv] = useState<boolean>(true);
  const [nextTrigger, setNextTrigger] = useState<boolean>(false);
  const [isCsvDownloaded, setIsCsvDownloaded] = useState<boolean>(false);

  const { user } = useAuth();
  const [loading, validatePayload, taskExecutionId] = useValidatePayload();
  const [sqsLoading, placeSqsMessage] = usePlaceSQSMessage();
  const [statusLoading, updateBulkNotificationStatus] =
    updateNotificationStatus();

  useEffect(() => {
    if (!nextTrigger) return;
    switch (currentStep) {
      case 0:
        setCurrentStep((prev) => prev + 1);
        setNextTrigger(false);
        break;

      case 1:
        const payload = framePayload(filters!);
        const validate = bulkEmailPayloadValidator(payload);

        if (validate) {
          showToast({
            type: 'warning',
            message: 'Filter validation failed',
          });
          setNextTrigger(false);
          break;
        }

        validatePayload(user?.access_token, payload, setValidTemplate).then(
          () => {
            setCurrentStep((prev) => prev + 1);
            setNextTrigger(false);
          },
        );
        break;

      case 2:
        setIsOpen(true);
        break;

      default:
        break;
    }
  }, [nextTrigger]);

  const renderStepComponent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <BulkEmailTemplateSelection
            token={user?.access_token}
            setValidTemplate={setValidTemplate}
            filters={filters!}
            setFilters={setFilters}
          />
        );
      case 1:
        return (
          <BulkEmailFilterSelection
            token={user?.access_token!}
            setValidTemplate={setValidTemplate}
            filters={filters!}
            setFilters={setFilters}
          />
        );
      case 2:
        return (
          <BulkEmailPreviewAndSendNotification
            token={user?.access_token!}
            filters={filters!}
            taskExecutionId={taskExecutionId}
            isvalidCSV={isValidCsv}
            isCsvDownloaded={setIsCsvDownloaded}
          />
        );
      default:
        return <></>;
    }
  };

  const setStatesToDefault = () => {
    setValidTemplate(false);
    isValidCsv(true);
    setIsCsvDownloaded(false);
    setNextTrigger(false);
  };

  const handleBackButton = () => {
    setCurrentStep(currentStep - 1);
    isValidCsv(true);
    setIsCsvDownloaded(false);
  };

  const handleModalCancelClick = async () => {
    setIsOpen(false);
    revertBulkEmailState(setFilters, setCurrentStep);
    setStatesToDefault();
    await updateBulkNotificationStatus(user?.access_token, taskExecutionId);
  };

  const handleModalYesClick = async () => {
    await placeSqsMessage(user?.access_token, taskExecutionId, setCurrentStep);
    setIsOpen(false);
    revertBulkEmailState(setFilters, setCurrentStep);
    setStatesToDefault();
  };

  const handleCancel = async () => {
    revertBulkEmailState(setFilters, setCurrentStep);
    setStatesToDefault();
    await updateBulkNotificationStatus(user?.access_token, taskExecutionId);
  };

  const checkDisabled = () => {
    if (!validTemplate || !validCsv || (!isCsvDownloaded && currentStep === 2))
      return true;
    return false;
  };

  if (loading || sqsLoading || statusLoading) return <Loading />;

  return (
    <div className="relative overflow-y-auto h-[500px] p-7 bg-secondary-pink-50 shadow rounded-xl">
      <div className="mt-5">{renderStepComponent(currentStep)}</div>

      <div className="absolute bottom-0 left-0 w-full px-5 py-4 bg-white flex justify-between items-center">
        {currentStep > 0 && (
          <button
            type="button"
            onClick={handleBackButton}
            className="bg-primary-blue-500 border border-primary-blue-500 text-white px-4 py-2 rounded me-5"
          >
            Back
          </button>
        )}
        <div className="w-full justify-center">
          <BulkEmailStepper currentStep={currentStep} steps={steps} />
        </div>

        <div className="flex justify-end w-auto">
          {currentStep > 0 && (
            <button
              type="button"
              className="bg-primary-blue-500 border border-primary-blue-500 text-white px-4 py-2 rounded me-5"
              onClick={() => handleCancel()}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            onClick={async () => {
              setNextTrigger(true);
            }}
            disabled={checkDisabled()}
            className={`bg-primary-blue-500 border border-primary-blue-500 text-white px-4 py-2 rounded 
              ${checkDisabled() ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {currentStep < steps.length - 1 ? 'Next' : 'Proceed'}
          </button>
        </div>
      </div>

      <CustomModal
        isOpen={open}
        onClose={() => setIsOpen(false)}
        header="Confirmation"
        footer={
          <>
            <button
              className={
                'mr-4 bg-primary-blue-500 text-white px-4 py-2 w-24 rounded'
              }
              onClick={() => handleModalYesClick()}
            >
              Yes
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 w-24 px-4 py-2 rounded"
              onClick={() => handleModalCancelClick()}
            >
              Cancel
            </button>
          </>
        }
      >
        <div>
          Are you sure want to send the email to those patients listed in the
          CSV ?
        </div>
      </CustomModal>
    </div>
  );
};

export default BulkEmailNotification;
